// ============================================================================
// CHEST
// ============================================================================

import CONFIG from '../config.js';
import { lerp, easeOutElastic } from '../utils/helpers.js';
import { DetachedDoor } from './DetachedDoor.js';
import { BreakableEntity } from './BreakableEntity.js';

export class Chest extends BreakableEntity {
    constructor(x, y, physics = null) {
        super(x, y, 50, 35, physics);
        this.lidHeight = 20; // Increased from 8 to reduce tunneling
        this.lidAngle = 0;
        this.targetLidAngle = 0;
        this.isOpen = false;
        this.isAnimating = false;
        this.animationTime = 0;
        this.animationDuration = 0;
        this.wobbleTime = 0;
        this.wobbleAmplitude = 0;
        this.tilt = 0;
        this.overshoot = 0;
        
        // Create Matter.js body (static when not broken)
        if (physics) {
            this.body = physics.createRectangle(x, y, this.width, this.height, {
                isStatic: true,
                frictionAir: 0,
                friction: 0,
                restitution: 0
            });
            physics.linkEntity(this, this.body);
        }
    }

    canInteract() {
        return !this.isAnimating;
    }

    performAction(level, aggression, game) {
        this.isOpen = !this.isOpen;
        this.targetLidAngle = this.isOpen ? 90 : 0;

        this.isAnimating = true;
        this.animationTime = 0;
        this.animationDuration = CONFIG.doorOpenSpeed[level];
        this.overshoot = CONFIG.doorOvershoot[level];

        if (level >= 1) {
            this.wobbleAmplitude = level * 2;
            this.wobbleTime = 0.5;
        }

        // Tilt increases with aggression (aggression already starts at 0 after interactionBaselineTime)
        if (aggression > 0) {
            this.tilt += aggression * 0.5;
            this.tilt = Math.min(this.tilt, CONFIG.maxTilt);
        }
    }

    getBreakThreshold() {
        return CONFIG.chestBreakThreshold;
    }

    onBreak(game) {
        let chestColor = '#8B4513'; // Brown chest color
        if (this.damage >= CONFIG.chestDamageThresholds[2]) {
            chestColor = '#4B2010';
        } else if (this.damage >= CONFIG.chestDamageThresholds[1]) {
            chestColor = '#6B3410';
        } else if (this.damage >= CONFIG.chestDamageThresholds[0]) {
            chestColor = '#8B4010';
        }

        // Create detached lid (as a door)
        const lidX = this.x;
        const lidY = this.y - this.height / 2;
        const openAmount = this.lidAngle / 90;
        const lidOffsetY = -openAmount * 10;

        // Cap force amount for spawn velocity (prevents tunneling), but damage was already applied uncapped
        const cappedForce = Math.min(this.lastDamageAmount, CONFIG.throwableMaxAggression);
        
        game.detachedDoors.push(new DetachedDoor(
            lidX,
            lidY + lidOffsetY,
            this.width,
            this.lidHeight,
            chestColor,
            false, // isLeftDoor
            cappedForce,
            this.physics
        ));
    }

    update(dt) {
        // Sync position from physics body (if not broken)
        if (this.body && !this.isBroken) {
            this.x = this.body.position.x;
            this.y = this.body.position.y;
        }
        
        if (this.isAnimating) {
            this.animationTime += dt;
            const progress = Math.min(this.animationTime / this.animationDuration, 1);

            const easedProgress = easeOutElastic(progress);
            const overshootAngle = this.targetLidAngle + (this.isOpen ? this.overshoot : -this.overshoot);

            if (this.overshoot > 0) {
                if (progress < 0.7) {
                    this.lidAngle = lerp(this.isOpen ? 0 : 90, overshootAngle, progress / 0.7);
                } else {
                    this.lidAngle = lerp(overshootAngle, this.targetLidAngle, (progress - 0.7) / 0.3);
                }
            } else {
                this.lidAngle = lerp(this.isOpen ? 0 : 90, this.targetLidAngle, easedProgress);
            }

            if (progress >= 1) {
                this.lidAngle = this.targetLidAngle;
                this.isAnimating = false;
            }
        }

        if (this.wobbleTime > 0) {
            this.wobbleTime -= dt;
        }
    }

    draw(ctx, camera) {
        ctx.save();

        let wobbleX = 0;
        let wobbleY = 0;
        if (this.wobbleTime > 0) {
            wobbleX = Math.sin(this.wobbleTime * 30) * this.wobbleAmplitude * (this.wobbleTime / 0.5);
            wobbleY = Math.cos(this.wobbleTime * 30) * this.wobbleAmplitude * (this.wobbleTime / 0.5);
        }

        const drawX = this.x + wobbleX;
        const drawY = this.y + wobbleY;

        ctx.translate(drawX, drawY);
        ctx.rotate((this.tilt * Math.PI) / 180);
        ctx.translate(-drawX, -drawY);

        // --- 3/4 PERSPECTIVE SETTINGS ---
        const depth = 10;
        const heightOffset = 25; 
        
        let chestColor = '#8B4513';
        if (this.damage >= CONFIG.chestDamageThresholds[2]) chestColor = '#4B2010';
        else if (this.damage >= CONFIG.chestDamageThresholds[1]) chestColor = '#6B3410';
        else if (this.damage >= CONFIG.chestDamageThresholds[0]) chestColor = '#8B4010';

        const darkerColor = '#3d1c07';
        const topColor = '#a65d2e';

        // 1. Draw SIDE/THICKNESS
        ctx.fillStyle = darkerColor;
        ctx.fillRect(drawX - this.width / 2, drawY - this.height / 2 - heightOffset, this.width, this.height + heightOffset);

        // 2. Draw TOP SURFACE (Visible when closed)
        if (!this.isOpen && !this.isBroken) {
            ctx.fillStyle = topColor;
            ctx.fillRect(drawX - this.width / 2, drawY - this.height / 2 - heightOffset, this.width, depth);
            ctx.strokeStyle = '#000';
            ctx.strokeRect(drawX - this.width / 2, drawY - this.height / 2 - heightOffset, this.width, depth);
        }

        // 3. Draw FRONT FACE
        const frontY = drawY - this.height / 2 - heightOffset + depth;
        const frontHeight = this.height + heightOffset - depth;

        ctx.fillStyle = chestColor;
        ctx.fillRect(drawX - this.width / 2, frontY, this.width, frontHeight);
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.strokeRect(drawX - this.width / 2, frontY, this.width, frontHeight);

        // Metal bands
        ctx.fillStyle = '#5A5A5A';
        ctx.fillRect(drawX - this.width / 2, frontY + 5, this.width, 3);
        ctx.fillRect(drawX - this.width / 2, frontY + frontHeight - 8, this.width, 3);

        if (!this.isBroken) {
            // Interior when lid opens
            if (this.lidAngle > 0) {
                ctx.fillStyle = '#654321';
                ctx.fillRect(drawX - this.width / 2 + 2, frontY + 2, this.width - 4, frontHeight - 4);
            }

            // Draw lid (old-style treasure chest: opens upward, hinged at back edge)
            ctx.save();
            // Hinge at back edge (top of chest, furthest from camera)
            // In 3/4 perspective, the back is at the top of the top surface
            const topSurfaceY = drawY - this.height / 2 - heightOffset;
            ctx.translate(drawX, topSurfaceY); // Hinge at back edge of top surface
            ctx.rotate((-this.lidAngle * Math.PI) / 180); // Negative angle to open upward/backward
            
            // Draw lid (extends forward from hinge)
            ctx.fillStyle = chestColor;
            ctx.fillRect(-this.width / 2, 0, this.width, this.lidHeight);
            ctx.strokeStyle = '#000';
            ctx.strokeRect(-this.width / 2, 0, this.width, this.lidHeight);
            
            // Top surface of lid (visible when open)
            if (this.lidAngle > 0) {
                ctx.fillStyle = topColor;
                ctx.fillRect(-this.width / 2, 0, this.width, depth);
                ctx.strokeStyle = '#000';
                ctx.strokeRect(-this.width / 2, 0, this.width, depth);
            }

            // Lock (on front edge of lid when closed)
            ctx.fillStyle = '#FFD700';
            ctx.beginPath(); ctx.arc(0, this.lidHeight / 2, 3, 0, Math.PI * 2); ctx.fill();
            ctx.restore();
        } else {
            // Broken interior
            ctx.fillStyle = '#654321';
            ctx.fillRect(drawX - this.width / 2 + 2, frontY + 2, this.width - 4, frontHeight - 4);
        }

        // Cracks
        if (this.damage >= CONFIG.chestDamageThresholds[0]) {
            ctx.strokeStyle = '#000';
            ctx.lineWidth = 1;
            ctx.globalAlpha = 0.5;
            const numCracks = Math.min(Math.floor(this.damage / 5), 6);
            for (let i = 0; i < numCracks; i++) {
                const x1 = drawX + (Math.random() - 0.5) * this.width;
                const y1 = frontY + (Math.random() - 0.5) * frontHeight;
                ctx.beginPath(); ctx.moveTo(x1, y1);
                ctx.lineTo(x1 + (Math.random() - 0.5) * 15, y1 + (Math.random() - 0.5) * 15);
                ctx.stroke();
            }
            ctx.globalAlpha = 1;
        }

        ctx.restore();
    }
}
